import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  MdPersonAdd,
  MdVerifiedUser,
  MdSearch,
  MdStar,
  MdPerson,
  MdThumbUp,
  MdThumbDown,
  MdNoteAdd,
} from 'react-icons/md';
import { FaPlus } from 'react-icons/fa';
import './Deals.scss';

const Deals = () => {
  const navigate = useNavigate();

  const baseURL = 
    process.env.NODE_ENV === 'production' 
    ? process.env.REACT_APP_API_URL_PROD 
    : process.env.REACT_APP_API_URL_LOCAL;

  const [formData, setFormData] = useState({
    score: '',
    leadPeriod: '',
    selectedLeadStatus: '',
    selectedDND: '',
    selectedSource: '',
    dealName: '',
    dealValue: '',
    wonValue: '',
    lostValue: '',
  });
  const [deals, setDeals] = useState([]);
  const [isOpenDeal, setIsOpenDeal] = useState(false);
  const [isAddNotes, setAddNotes] = useState(false);

  const leadStatusScoreMap = {
    Lead: '10%',
    'Qualified Lead': '30%',
    Prospect: '50%',
    'Most Prospective': '80%',
    Customer: '100%',
  };

  const leadStatusPeriodMap = {
    Lead: '6 months',
    'Qualified Lead': '3 months',
    Prospect: '4 months',
    'Most Prospective': '3 months',
  };

  useEffect(() => {
    const fetchDeals = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await fetch(`${baseURL}/api/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
         if (!response.ok && response.status >= 500) {
        throw new Error(`Server error (${response.status})`);
      }

      // Silently return if it's 204 or 404 (no content / not found)
      if (response.status === 204 || response.status === 404) {
        console.log(`No user data found (Status: ${response.status})`);
        return;
      }

      const data = await response.json();

      if (!data || typeof data !== "object") {
        console.log("Invalid data format received");
        return;
      }
        if (Array.isArray(data?.details)) setDeals(data.details);
        else alert(data.message || 'Failed to load deals');
      } catch (error) {
        console.error('Error fetching deals:', error);
      }
    };
    fetchDeals();
  }, []);

  const handleLeadStatusChange = (e) => {
    const status = e.target.value;
    const score = leadStatusScoreMap[status] || '';
    const leadPeriod = leadStatusPeriodMap[status] || '';
    const wonValue = status === 'Won' ? formData.dealValue : '';
    const lostValue = status === 'Lost' ? formData.dealValue : '';
    setFormData((prev) => ({
      ...prev,
      selectedLeadStatus: status,
      score,
      leadPeriod,
      wonValue,
      lostValue,
    }));
  };

  const handleInputChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${baseURL}/api/user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          leadstatus: formData.selectedLeadStatus,
          donotdisturb: formData.selectedDND,
          source: formData.selectedSource,
          dealname: formData.dealName,
          dealvalue: formData.dealValue,
          won: formData.wonValue,
          lost: formData.lostValue,
          leadperiod: formData.leadPeriod,
        }),
      });
      const data = await res.json();
      if (res.ok) alert('Updated successfully!');
      else alert(data.message);
    } catch (err) {
      console.error('Error submitting:', err);
    }
  };

  const leadStatuses = [
    'Lead',
    'Qualified Lead',
    'Prospect',
    'Most Prospective',
    'Customer',
    'Won',
    'Lost',
  ];
  const [activeTab, setActiveTab] = useState('Deals');
  const groupedDeals = leadStatuses.map((status) => ({
    status,
    deals: deals.filter((deal) => deal.leadstatus === status),
  }));

  const getDealIcon = (title) => {
    const iconProps = { color: '#Fcab12', fontSize: '17px' };
    const icons = {
      Lead: <MdPersonAdd style={iconProps} />,
      'Qualified Lead': <MdVerifiedUser style={iconProps} />,
      Prospect: <MdSearch style={iconProps} />,
      'Most Prospective': <MdStar style={iconProps} />,
      Customer: <MdPerson style={iconProps} />,
      Won: <MdThumbUp style={iconProps} />,
      Lost: <MdThumbDown style={iconProps} />,
    };
    return icons[title] || null;
  };

  return (
    <Box className="deal-container">
      <div className="crm-nav">
        <div
          key={'Dashboard'}
          className={`crm-menu ${activeTab === 'Dashboard' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('Dashboard');
            navigate(`/crm`);
          }}
        >
          Dashboard
        </div>
        <div
          key={'Deals'}
          className={`crm-menu ${activeTab === 'Deals' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('Deals');
            navigate(`/deals`);
          }}
        >
          Deals
        </div>
        <div
          key={'Activities'}
          className={`crm-menu ${activeTab === 'Activities' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('Activities');
            navigate(`/activities`);
          }}
        >
          Activities
        </div>
      </div>

      {/* <h2 className="all-deals">All Deals</h2> */}

      <Grid
        container
        spacing={2}
        sx={{ flexWrap: 'nowrap', overflowX: 'auto', width: '100%', pb: 2, px: 2 }}
      >
        {groupedDeals.map(({ status, deals }) => (
          <Grid item xs={12} sm={6} md={4} key={status}>

            {/* Status header box */}
            <div className={`status-header status-${status.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="status-title">
                {getDealIcon(status)}
                <p className="deal">{status}</p>
              </div>
            </div>

            {/* Deals container below the header */}
            <Box className="deal-list">
              {deals.length > 0 ? (
                deals.map((deal, idx) => (
                  <Box key={idx} className="deal-box no-deal-box">
                    <div>{deal.companyname}</div>
                    <div className="deal-info">
                      <div>${deal.dealvalue}</div>
                      <div className="deal-period">
                        {status === 'Won' || status === 'Lost' ? 'Closed' : `Closes in ${deal.leadperiod}`}
                      </div>

                    </div>
                    <div className="deal-note">
                      <Typography variant="caption">{deal.dealname}</Typography>
                      {/* <Tooltip title="Add Note">
                        <IconButton onClick={() => setAddNotes(true)}>
                          <MdNoteAdd size={18} />
                        </IconButton>
                      </Tooltip> */}
                    </div>
                  </Box>
                ))
              ) : (
                <Box className="deal-box no-deal-box">
                  <div className="deal-info">No {status} deals</div>
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />

                </Box>
              )}
            </Box>

          </Grid>
        ))}
      </Grid>

      {isOpenDeal && (
        <div className="deal-popup">
          <div className="overlay" onClick={() => setIsOpenDeal(false)}></div>
          <div className="deal-popup-box">
            <div className="popup-headers">
              <button className="close-button" onClick={() => setIsOpenDeal(false)}>
                ✕
              </button>
            </div>
            <div className="popup-heading">
              <h2 style={{ color: '#FCB124' }}>ADD DEAL</h2>
            </div>
            <div className="popup-contents">
              <div className="form-row">
                <label className="label">Deal Name</label>
                <input
                  className="deal-input1"
                  value={formData.dealName}
                  onChange={handleInputChange('dealName')}
                  placeholder="Deal Name"
                />
              </div>
              <div className="form-row">
                <label className="label">Currency</label>
                <div className="form-inline">
                  <select>
                    <option>USD</option>
                    <option>INR</option>
                    <option>EUR</option>
                  </select>
                  <input
                    className="deal-input"
                    type="number"
                    value={formData.dealValue}
                    onChange={handleInputChange('dealValue')}
                    placeholder="Deal Value"
                  />
                </div>
              </div>
              <div className="form-row">
                <label className="label">Lead Status</label>
                <select
                  className="deal-input2"
                  value={formData.selectedLeadStatus}
                  onChange={handleLeadStatusChange}
                >
                  <option value="">Select Status</option>
                  {[...leadStatuses, 'Won', 'Lost'].map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label className="label">Source</label>
                <select
                  className="deal-input2"
                  value={formData.selectedSource}
                  onChange={handleInputChange('selectedSource')}
                >
                  <option value="">Select Source</option>
                  {[
                    'Email Campaign',
                    'Networking',
                    'Reference',
                    'LinkedIn',
                    'Cold Calling',
                    'Inbound',
                    'Social Media',
                  ].map((src) => (
                    <option key={src} value={src}>
                      {src}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label className="label">Do Not Disturb</label>
                <select
                  className="deal-input2"
                  value={formData.selectedDND}
                  onChange={handleInputChange('selectedDND')}
                >
                  <option value="">Select Option</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div className="form-actions">
                <button className="deal-save" onClick={handleSubmit}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isAddNotes && (
        <div className="deal-popup">
          <div className="overlay"></div>
          <div className="deal-popup-box">
            <div className="popup-headers">
              <button className="close-button" onClick={() => setAddNotes(false)}>
                ✕
              </button>
            </div>
            <h1 style={{ color: '#Fcab12' }}>Add Notes</h1>
            <textarea className="input1" placeholder="Add Notes"></textarea>
            <button className="note-save">Save</button>
          </div>
        </div>
      )}
    </Box>
  );
};

export default Deals;